import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AllocationService,
  CommonConstant,
  CommonUtility,
  DateFormat,
  DateUtility,
  ToastMessage,
  WaveService,
} from '../utility';
import { useDebounce, useDebouncedEffect } from './debounce';
import type { GroupedWaveAllocation, WaveDetailsResponse } from 'types/wave/detail';

export const WaveListHook = (
  status,
  warehouseId,
  search,
  refreshKey = 0,
  initialFilter = null,
) => {
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // This will have all the parsing done, we may need to show its value in the UI
  const [params, setParams] = useState({});

  const [filter, setFilter] = useState(() => ({
    limit: 10,
    page: 1,
    ...(initialFilter || {}),
  }));

  const finalSearch = useDebounce(search, 200);
  useDebouncedEffect(
    () => {
      if (!warehouseId) return;
      requestRef.current += 1;
      const currentRequestId = requestRef.current;

      setLoading(true);

      const fetchData = async () => {
        try {
          const params = { status, warehouseId, ...filter };

          if (search?.trim()) params.search = search.trim();

          if (params.presets) {
            let referenceDate = new Date();
            if (params.presets === 'yesterday') {
              referenceDate = new Date(referenceDate);
              referenceDate.setDate(referenceDate.getDate() - 1);
            }

            params.startDate = new Date(
              referenceDate.getFullYear(),
              referenceDate.getMonth(),
              referenceDate.getDate(),
              0,
              0,
              0,
            ).toISOString();

            params.endDate = new Date(
              referenceDate.getFullYear(),
              referenceDate.getMonth(),
              referenceDate.getDate(),
              23,
              59,
              59,
            ).toISOString();
          }

          if (params.dateRange) {
            const dates = params.dateRange.split(',');
            params.startDate = new Date(dates[0]).toISOString();
            params.endDate = new Date(dates[1]).toISOString();
          }

          delete params.dateRange;
          delete params.presets;

          setParams(params);

          if (!params.status) return;

          const res = await WaveService.waveList(params);

          // Only accept if it's still the latest request - For Race Condition!
          if (currentRequestId === requestRef.current) {
            setData(res?.data);
            setTotalData(res?.total);
            setTotalPages(res?.totalPages);
          }
        } catch (err) {
          if (currentRequestId === requestRef.current) {
            setError(err);
          }
        } finally {
          if (currentRequestId === requestRef.current) {
            setLoading(false);
          }
        }
      };

      fetchData();
    },
    [filter, status, warehouseId, finalSearch, refreshKey],
    300,
  );

  const pageChanged = page => {
    setFilter(prev => ({ ...prev, page }));
  };

  const filterChanged = (key, value) => {
    setFilter(prev => ({ ...prev, page: 1, [key]: value }));
  };

  const updateAllFilter = useCallback(obj => {
    setFilter(filter => ({
      ...filter,
      ...obj,
    }));
  }, []);

  return {
    data,
    loading,
    error,
    totalData,
    totalPages,
    filter,
    pageChanged,
    filterChanged,
    updateAllFilter,
    params,
  };
};

const isValidWaveSearch = search => !!(search && typeof search === 'string');

export const wavesAutocompleteHook = (search, warehouseId) => {
  const [data, setData] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        warehouseId,
      } as any;

      if (search) {
        params.search = search.trim();
      }

      const result = await WaveService.waveList(params);
      const mapped = result.data.map(d => ({ ...d, search }));
      setData(mapped);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData([]);
    if (isValidWaveSearch(search) && warehouseId) {
      fetchData();
    }
  }, [search, warehouseId]);

  return { data, error, loading };
};

export const AllocationHook = (status, warehouseId) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
    sort: {
      sortBy: 'createdAt',
      sortOrder: CommonConstant.sortDirection.descending,
    },
    search: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const { sort, ...filterParams } = filter;
      const trimmedSearch = filter.search?.trim();
      const params = {
        ...filterParams,
        ...sort,
        status,
        warehouseId,
      };
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }
      const res = await AllocationService.getAllocations(params);
      setData(res?.data?.allocations || []);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (warehouseId) {
        fetchData();
      }
    },
    [filter, status, warehouseId, refreshKey],
    300,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const onSortChange = sortObject => {
    if (!CommonUtility.isValidObject(sortObject)) return;
    const sortKey = Object.keys(sortObject)[0];
    const sortOrder = sortObject[sortKey];
    if (sortKey !== 'createdAt' || !sortOrder) return;

    setFilter(prev => ({
      ...prev,
      page: 1,
      sort: {
        sortBy: sortKey,
        sortOrder,
      },
    }));
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    loading,
    error,
    totalData,
    totalPages,
    filter,
    pageChanged,
    filterChanged,
    onSortChange,
    refresh,
  };
};

export const WaveRuleListHook = warehouseId => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: CommonConstant.defaultPageSize,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      let params = { ...filter, warehouseId };
      params = CommonUtility.removeEmptyFields(params);
      const res = await WaveService.waveRuleList(params);
      setData(res?.rules);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      fetchData();
    },
    [warehouseId, refreshKey, filter],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  const updateAllFilter = obj => {
    setFilter(filter => ({
      ...filter,
      page: 1,
      ...obj,
    }));
  };

  return {
    data,
    loading,
    error,
    totalData,
    totalPages,
    filter,
    pageChanged,
    filterChanged,
    refresh,
    updateAllFilter,
  };
};

type WaveDetailHookReturnType = {
  data: WaveDetailsResponse['data'];
  loading: boolean;
  error: Error | null;
  allocations: GroupedWaveAllocation[];
  refresh: () => void;
  setLoading: (loading: boolean) => void;
  exporting: boolean;
  exportCSV: () => Promise<void>;
};

export const WaveDetailHook = (id: string): WaveDetailHookReturnType => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WaveDetailsResponse['data']>();
  const [error, setError] = useState(null);
  const [allocations, setAllocations] = useState<GroupedWaveAllocation[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res: WaveDetailsResponse = await WaveService.getById(id);
      setData(JSON.parse(JSON.stringify(res?.data)) || {});
      const items: GroupedWaveAllocation[] = [];

      (res?.data?.wave?.allocations || [])?.forEach(item => {
        const itemExistAtIndex = items.findIndex(
          x => x.inventory.product.id === item.inventory.product.id,
        );
        if (itemExistAtIndex > -1) {
          if (CommonUtility.isValidArray(items[itemExistAtIndex].sameItems)) {
            items[itemExistAtIndex].sameItems.push(item);
          } else {
            items[itemExistAtIndex].sameItems = [items[itemExistAtIndex], item];
          }
        } else {
          items.push(item);
        }
      });
      setAllocations(items);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, refreshKey]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const response = await WaveService.exportWave(id);
      const date = DateUtility.formatDate(new Date(), DateFormat.dateTime);
      const fileName = `wave-${data?.wave?.waveNumber}-${date}`;
      CommonUtility.downloadCSV(response, fileName);
      ToastMessage.success('The wave csv has been downloaded successfully.');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setExporting(false);
    }
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    loading,
    error,
    refresh,
    setLoading,
    allocations,
    exporting,
    exportCSV,
  };
};

export const SortedWaveListHook = warehouseId => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 10,
    page: 1,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        ...filter,
        warehouseId,
      };
      const res = await WaveService.sortedWaveList(params);
      setData(res?.data?.allocations || []);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (warehouseId) fetchData();
    },
    [filter, warehouseId, refreshKey],
    100,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  return {
    data,
    loading,
    error,
    totalData,
    totalPages,
    filter,
    pageChanged,
    filterChanged,
    refresh,
  };
};
